(() => {
  'use strict';
  const root = document.getElementById('motousher-garage');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const dialog = root.querySelector('dialog');
  const launcher = root.querySelector('.garage-launcher');
  const status = root.querySelector('.garage-status');
  const editor = root.querySelector('.garage-editor');
  const close = root.querySelector('.garage-close');
  const customerId = root.dataset.customerId;
  const promptKey = `garage-prompt:${customerId}`;
  const path = root.dataset.proxyPath.replace(/\/$/, '');
  let selected = [];
  let token = '';
  let ready = false;
  let loading = false;
  let saving = false;
  let searchSequence = 0;
  let searchTimer;
  let searchController;
  let savedIds = '';

  function message(text, error = false) {
    status.textContent = text;
    status.dataset.error = String(error);
  }

  function dismissed() {
    // Storage may be blocked; the garage still works without prompt persistence.
    try { return sessionStorage.getItem(promptKey) === 'dismissed'; }
    catch { return false; }
  }

  function dismiss() {
    try { sessionStorage.setItem(promptKey, 'dismissed'); }
    catch { /* Optional session-only prompt preference. */ }
  }

  async function request(suffix = '', options = {}) {
    const response = await fetch(path + suffix, {
      credentials: 'same-origin',
      ...options,
      redirect: 'manual',
      headers: { Accept: 'application/json', ...options.headers },
    });
    if (response.type === 'opaqueredirect' || response.redirected || (response.status >= 300 && response.status < 400)) {
      throw new Error('The garage request was redirected. Nothing has been confirmed saved. Please contact support to remove redirects from the Shopify app proxy target.');
    }
    if (response.status === 404) {
      throw new Error('The garage is not available yet. Please contact support to check the Shopify app proxy and portal setup.');
    }
    if (!(response.headers.get('content-type') || '').includes('application/json')) {
      throw new Error('The garage is unavailable. Check that you are signed in and try again.');
    }
    const body = await response.text();
    let data;
    try {
      data = JSON.parse(body);
    } catch (error) {
      if (!(error instanceof SyntaxError)) throw error;
      throw new Error('The garage received an invalid response. Please contact support or try again later.');
    }
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error('The garage received an invalid response. Please contact support or try again later.');
    }
    if (!response.ok) {
      const errors = data.errors ? Object.values(data.errors).flat().join(' ') : '';
      throw new Error(errors || data.message || 'The garage request failed. Please try again.');
    }
    if (options.method === 'POST' && (data.saved !== true || !Array.isArray(data.collections))) {
      throw new Error('The garage did not confirm your save. Please try again or contact support to check that the portal and theme are both updated.');
    }
    return data;
  }

  function open() {
    if (!dialog.open) dialog.showModal();
  }

  close.addEventListener('click', () => { dismiss(); dialog.close(); });
  dialog.addEventListener('cancel', dismiss);
  dialog.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      dismiss();
      dialog.close();
    }
  });
  launcher.addEventListener('click', () => {
    open();
    if (editor && !ready && !loading) load(false);
  });
  if (!editor) return;

  const save = editor.querySelector('.garage-save');
  const input = editor.querySelector('input');
  const results = editor.querySelector('.garage-results');
  const bikes = editor.querySelector('.garage-selected');
  const caption = editor.querySelector('.garage-results-caption');
  const retry = editor.querySelector('.garage-retry');
  const launcherCount = root.querySelector('.garage-launcher-count');
  const iconTemplate = root.querySelector('.garage-bike-icon');

  function ids(bikeList) {
    return JSON.stringify(bikeList.map(bike => bike.id));
  }

  function bikeMark() {
    const mark = document.createElement('span');
    mark.className = 'garage-bike-mark';
    mark.append(iconTemplate.content.cloneNode(true));
    return mark;
  }

  function collectionPath(handle) {
    if (typeof handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(handle)) return null;
    const storefrontRoot = root.dataset.storefrontRoot || '/';
    return storefrontRoot.replace(/\/?$/, '/') + 'collections/' + encodeURIComponent(handle);
  }

  function bikeCopy(title, label, handle) {
    const copy = document.createElement('span');
    copy.className = 'garage-bike-copy';
    if (label) {
      const kicker = document.createElement('span');
      kicker.className = 'garage-bike-label';
      kicker.textContent = label;
      copy.append(kicker);
    }
    const href = collectionPath(handle);
    const name = document.createElement(href ? 'a' : 'span');
    name.className = 'garage-bike-title';
    if (href) name.href = href;
    name.textContent = title;
    copy.append(name);
    return copy;
  }

  function searchNotice(text) {
    results.replaceChildren();
    const row = document.createElement('li');
    row.className = 'garage-result-empty';
    row.textContent = text;
    results.append(row);
  }

  function render() {
    bikes.replaceChildren();
    selected.forEach((bike, index) => {
      const row = document.createElement('li');
      const remove = document.createElement('button');
      remove.className = 'garage-remove';
      remove.type = 'button';
      remove.textContent = 'Remove';
      remove.setAttribute('aria-label', `Remove ${bike.title}`);
      remove.disabled = saving;
      remove.addEventListener('click', () => {
        selected.splice(index, 1);
        message('Bike removed. Save to update your garage.');
        render();
        input.focus();
        search();
      });
      row.append(bikeMark(), bikeCopy(bike.title, index === 0 ? 'Primary' : 'Secondary', bike.handle), remove);
      bikes.append(row);
    });
    for (let index = selected.length; index < 2; index++) {
      const row = document.createElement('li');
      row.className = 'garage-empty-slot';
      row.append(bikeMark(), bikeCopy(ready ? 'Add a bike' : loading ? 'Loading...' : 'No bike selected', index === 0 ? 'Primary' : 'Secondary'));
      bikes.append(row);
    }
    save.disabled = !ready || saving || selected.some(bike => !bike.id) || ids(selected) === savedIds;
    save.firstElementChild.textContent = saving ? 'Saving your garage...' : 'Save my garage';
    editor.setAttribute('aria-busy', String(loading || saving));
    input.disabled = !ready || saving;
    results.querySelectorAll('button').forEach(button => {
      button.disabled = saving || selected.length >= 2 || selected.some(bike => bike.id === button.dataset.id);
      button.textContent = selected.some(bike => bike.id === button.dataset.id) ? 'Added' : selected.length >= 2 ? 'Full' : '+ Add';
    });
  }

  async function search() {
    clearTimeout(searchTimer);
    if (searchController) searchController.abort();
    const sequence = ++searchSequence;
    if (!ready || saving) return;
    searchController = new AbortController();
    retry.hidden = true;
    results.setAttribute('aria-busy', 'true');
    caption.textContent = 'Searching...';
    searchNotice('Searching bike collections...');
    try {
      const data = await request('/collections?q=' + encodeURIComponent(input.value), { signal: searchController.signal });
      if (sequence !== searchSequence) return;
      results.replaceChildren();
      data.collections.forEach(bike => {
        const row = document.createElement('li');
        const add = document.createElement('button');
        add.className = 'garage-add';
        add.type = 'button';
        add.textContent = 'Add';
        add.dataset.id = bike.id;
        add.setAttribute('aria-label', `Add ${bike.title}`);
        add.addEventListener('click', () => {
          if (saving || selected.length >= 2 || selected.some(item => item.id === bike.id)) return;
          selected.push(bike);
          message(selected.length === 2 ? 'Two bikes selected. Save to update your garage.' : 'Bike added. Save to update your garage.');
          render();
        });
        row.append(bikeMark(), bikeCopy(bike.title), add);
        results.append(row);
      });
      caption.textContent = data.collections.length ? '' : 'No matching bikes';
      if (!data.collections.length) searchNotice('No bikes found. Try a different brand or model.');
      render();
    } catch (error) {
      if (error.name !== 'AbortError' && sequence === searchSequence) {
        message(error.message, true);
        caption.textContent = 'Search unavailable';
        searchNotice('We could not load bike collections. Please try again.');
        retry.hidden = false;
      }
    } finally {
      if (sequence === searchSequence) results.setAttribute('aria-busy', 'false');
    }
  }

  input.addEventListener('input', () => {
    clearTimeout(searchTimer);
    if (searchController) searchController.abort();
    ++searchSequence;
    searchTimer = setTimeout(search, 250);
  });

  async function load(autoPrompt) {
    loading = true;
    retry.hidden = true;
    render();
    message('Loading your garage...');
    try {
      const data = await request();
      selected = data.collections;
      savedIds = ids(selected);
      token = data.write_token;
      ready = true;
      launcherCount.textContent = String(selected.length);
      launcherCount.hidden = selected.length === 0;
      render();
      message(selected.some(bike => !bike.id)
        ? 'A saved bike has no collection ID. Remove it and choose it again before saving.'
        : '');
      if (autoPrompt && selected.length === 0 && !dismissed()) open();
      if (dialog.open) await search();
    } catch (error) {
      message(error.message, true);
      caption.textContent = 'Garage unavailable';
      retry.hidden = false;
    } finally {
      loading = false;
      render();
    }
  }

  retry.addEventListener('click', () => {
    if (ready) {
      message('');
      search();
    } else if (!loading) load(false);
  });
  dialog.addEventListener('close', () => {
    clearTimeout(searchTimer);
    if (searchController) searchController.abort();
    ++searchSequence;
    results.setAttribute('aria-busy', 'false');
  });
  launcher.addEventListener('click', () => { if (ready) search(); });
  save.addEventListener('click', async () => {
    if (!ready || saving || selected.some(bike => !bike.id)) return;
    saving = true;
    clearTimeout(searchTimer);
    if (searchController) searchController.abort();
    ++searchSequence;
    results.setAttribute('aria-busy', 'false');
    render();
    message('Saving your garage...');
    try {
      const data = await request('', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Garage-Token': token },
        body: JSON.stringify({ collection_ids: selected.map(bike => bike.id) }),
      });
      selected = data.collections;
      savedIds = ids(selected);
      launcherCount.textContent = String(selected.length);
      launcherCount.hidden = selected.length === 0;
      dismiss();
      message('Your garage has been saved.');
    } catch (error) {
      message(error.message, true);
      // A fresh identity-bound write token is required after it expires.
      if (error.message.includes('Reload the garage')) {
        ready = false;
        retry.hidden = false;
      }
    } finally {
      saving = false;
      render();
    }
  });
  render();
  load(true);
})();
